<template>
  <div>
    <div class="flex items-start justify-center">
      <div class="w-full h-auto bg-white rounded-3xl shadow-sm">
        <div class="flex flex-col p-3 sm:p-4">
          <div class="flex items-start justify-between w-full mb-2 sm:mb-3">
            <div
              class="w-full text-xs sm:text-sm font-medium text-neutral-900 cursor-pointer"
              @click="!is_cross_post ? openPostDetail() : null"
            >
              <div class="timestamp-container">
                <div v-if="tab !== 1" class="timestamp-badge">
                  <div class="timestamp-icon">
                    <v-icon x-small>mdi-calendar-clock</v-icon>
                  </div>
                  <div class="timestamp-info">
                    <span v-if="tab === 0" class="timestamp-label"
                      >Published:</span
                    >
                    <span v-else class="timestamp-label">Created:</span>
                    <span class="timestamp-value">{{
                      formatDate(post.created_at.seconds * 1000)
                    }}</span>
                  </div>
                </div>

                <div v-else-if="tab === 1" class="timestamp-badge scheduled">
                  <div class="timestamp-icon">
                    <v-icon x-small>mdi-calendar-check</v-icon>
                  </div>
                  <div class="timestamp-info">
                    <span class="timestamp-label">Scheduled:</span>
                    <span class="timestamp-value">{{
                      getScheduledDate(post.scheduled_at)
                    }}</span>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="!is_cross_post" class="flex gap-2 items-center">
              <div
                v-if="
                  checkUID &&
                  (!post.post_status ||
                    (post.post_status && post.post_status !== 'deleted'))
                "
                @click="deletePost(post)"
                class="bg-[#F2F6FA] rounded-md p-1 sm:p-[5px] cursor-pointer hover:bg-slate-200"
              >
                <img
                  src="../../assets/img/post-icons/deleteicon.svg"
                  alt="delete"
                  class="w-4 h-4 sm:w-5 sm:h-5"
                />
              </div>
              <div
                class="bg-[#F2F6FA] cursor-pointer rounded-md p-1 sm:p-[5px] hover:bg-slate-200"
                @click="copyLink()"
              >
                <img
                  src="../../assets/img/post-icons/copyicon.svg"
                  alt="copy"
                  class="w-4 h-4 sm:w-5 sm:h-5"
                />
              </div>
              <a
                v-if="showPreviewIcon"
                class="bg-[#F2F6FA] cursor-pointer rounded-md p-1 sm:px-2 sm:py-[0.1rem] hover:bg-slate-200"
                @click="openPostDetail()"
              >
                <v-icon class="eye-icon" small> mdi-eye </v-icon>
              </a>
            </div>
          </div>

          <div class="flex flex-col md:flex-row w-full mb-2 sm:mb-3">
            <div
              v-if="carousels.length"
              class="w-full md:w-[150px] md:mr-3 mb-2 md:mb-0 rounded-lg overflow-hidden"
            >
              <v-carousel
                hide-delimiters
                v-model="carousel"
                :show-arrows="carousels.length > 1"
                height="150px"
                class="media-carousel-side"
              >
                <v-carousel-item
                  v-for="(item, index) in carousels"
                  :key="index"
                >
                  <div
                    class="h-full d-flex align-center justify-center bg-slate-100"
                  >
                    <v-img
                      v-if="item.type == 'image'"
                      :src="item.src"
                      contain
                      max-height="150px"
                    />
                    <video-auto-play
                      v-if="item.type == 'video'"
                      :autoplay="false"
                      :videoSrc="item.src"
                      style="
                        max-height: 150px;
                        max-width: 100%;
                        object-fit: contain;
                      "
                    />
                  </div>
                </v-carousel-item>
              </v-carousel>
            </div>

            <div
              :class="{
                'md:flex-1': carousels.length > 0,
                'w-full': carousels.length === 0,
              }"
              class="text-xs sm:text-sm text-slate-700"
            >
              <div
                v-if="post.name !== ''"
                class="text-sm sm:text-base font-semibold text-slate-800 mb-1"
              >
                {{ post.name }}
              </div>
              <span class="text-slate-600">
                {{ showFullText ? post.text : shortText }}
              </span>
              <button
                v-if="post.text.length > 100"
                @click="showFullText = !showFullText"
                class="text-xs text-blue-600 ml-1 hover:underline"
              >
                {{ showFullText ? "Show Less" : "Show More" }}
              </button>
              <div class="text-slate-500 mt-1 text-xs">
                {{ formatHashtags(post.hashtags) }}
              </div>
            </div>
          </div>

          <div
            class="flex items-center justify-between w-full mb-2 sm:mb-3 text-xs"
          >
            <div class="flex gap-1">
              <v-avatar
                tile
                size="18"
                v-for="(socialMedia, index) in post.social_media"
                :key="index"
              >
                <v-img
                  :src="socialMedia.logo_url"
                  :alt="socialMedia.name"
                ></v-img>
              </v-avatar>
            </div>
            <div
              v-if="
                post.configuration &&
                post.configuration.gmb_post_type === 'OFFER'
              "
              class="rounded-md p-1 sm:p-[5px] cursor-pointer"
              title="GMB Offer Post"
            >
              <v-icon medium color="#ffc204">mdi-tag</v-icon>
            </div>
            <div>
              <span
                v-if="post.post_status === 'deleted'"
                class="status-badge status-badge-deleted"
                >Deleted</span
              >
              <span
                v-else-if="post.post_status === 'draft'"
                class="status-badge status-badge-draft"
                >Draft</span
              >
              <span
                v-else-if="
                  post.when_to_post === 'schedule' && !isPostLive(post)
                "
                class="status-badge status-badge-scheduled"
                >Scheduled</span
              >
              <span
                v-else-if="
                  post.when_to_post === 'immediate' ||
                  (post.scheduled_at && isPostLive(post))
                "
                class="status-badge status-badge-published"
                >Published</span
              >
            </div>
          </div>

          <div
            v-if="postedLocationCount && !showOutput"
            class="text-xs font-medium text-slate-500 mb-2 w-full"
          >
            Posted to {{ postedLocationCount }} Locations
          </div>

          <div class="flex gap-2 items-center mb-2 sm:mb-3">
            <button
              v-if="!is_cross_post"
              class="flex items-center justify-center gap-1 px-3 py-1.5 text-xs text-slate-700 font-semibold bg-slate-100 hover:bg-slate-200 rounded-full"
              @click="
                post.post_status === 'draft'
                  ? editDraft(post.id)
                  : post.when_to_post !== 'immediate' && !isPostLive(post)
                  ? editPost(post.id)
                  : repost(post.id)
              "
            >
              <img
                src="../../assets/img/post-icons/repost.svg"
                alt=""
                class="w-3 h-3"
              />
              {{
                post.post_status === "draft"
                  ? "Edit"
                  : post.when_to_post !== "immediate" && !isPostLive(post)
                  ? "Edit"
                  : "Repost"
              }}
            </button>

            <button
              v-if="
                !post.post_status ||
                (post.post_status && post.post_status !== 'deleted')
              "
              class="flex items-center justify-center gap-1 px-3 py-1.5 text-xs text-slate-700 font-semibold bg-slate-100 hover:bg-slate-200 rounded-full"
              @click="showOutput = !showOutput"
            >
              <img
                src="../../assets/img/post-icons/graph.svg"
                alt=""
                class="w-3 h-3"
              />
              Stats
              <v-icon x-small v-if="showOutput">mdi-chevron-up</v-icon>
              <v-icon x-small v-else>mdi-chevron-down</v-icon>
            </button>
          </div>

          <div v-if="showOutput" class="mt-1 sm:mt-2 w-full">
            <div
              v-if="postedLocationCount"
              class="text-xs font-medium text-slate-600 mb-2 w-full"
            >
              Posted to Locations: {{ postedLocationCount }} | Break-up below:
              <table class="platform-status-table mt-1 sm:mt-2 w-full text-xs">
                <thead>
                  <tr>
                    <th class="text-left px-1 py-0.5 sm:px-2 sm:py-1">
                      Platform
                    </th>
                    <th class="text-center px-1 py-0.5 sm:px-2 sm:py-1">
                      Published ({{ getTotalStatusCount("published") }})
                    </th>
                    <th class="text-center px-1 py-0.5 sm:px-2 sm:py-1">
                      Failed ({{ getTotalStatusCount("failed") }})
                    </th>
                    <th class="text-center px-1 py-0.5 sm:px-2 sm:py-1">
                      Pending ({{ getTotalStatusCount("pending") }})
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(stats, platform) in platformStatusMap"
                    :key="platform"
                  >
                    <td class="px-1 py-0.5 sm:px-2 sm:py-1 capitalize">
                      {{ formatPlatformName(platform) }}
                    </td>
                    <td class="text-center px-1 py-0.5 sm:px-2 sm:py-1">
                      <span
                        :class="{
                          'text-green-600 font-semibold cursor-pointer':
                            getStatusCount(platform, 'published') > 0,
                        }"
                        @click="
                          getStatusCount(platform, 'published') > 0 &&
                            showLocationDetails(platform, 'published')
                        "
                      >
                        {{ getStatusCount(platform, "published") }}
                      </span>
                    </td>
                    <td class="text-center px-1 py-0.5 sm:px-2 sm:py-1">
                      <span
                        :class="{
                          'text-red-600 font-semibold cursor-pointer':
                            getStatusCount(platform, 'failed') > 0,
                        }"
                        @click="
                          getStatusCount(platform, 'failed') > 0 &&
                            showLocationDetails(platform, 'failed')
                        "
                      >
                        {{ getStatusCount(platform, "failed") }}
                      </span>
                    </td>
                    <td class="text-center px-1 py-0.5 sm:px-2 sm:py-1">
                      <div
                        v-if="getStatusCount(platform, 'pending') > 0"
                        class="pending-loader-container cursor-pointer"
                        :class="{
                          'text-blue-600 font-semibold':
                            getStatusCount(platform, 'pending') > 0,
                        }"
                        @click="showLocationDetails(platform, 'pending')"
                      >
                        {{ getStatusCount(platform, "pending") }}
                        <div class="pending-loader"></div>
                      </div>
                      <span
                        v-else
                        :class="{
                          'text-blue-600 font-semibold':
                            getStatusCount(platform, 'pending') > 0,
                        }"
                      >
                        {{ getStatusCount(platform, "pending") }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <post-output
              :scheduled_at="
                post.scheduled_at
                  ? post.scheduled_at.seconds
                    ? formatDate(post.scheduled_at.seconds * 1000)
                    : post.scheduled_at
                  : ''
              "
              :postUID="post.uid"
              :postId="post.id"
              :postwid="post.wid"
              :post="post"
              class="mt-2"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Location Details Dialog -->
    <v-dialog v-model="showLocationDialog" max-width="600px">
      <v-card>
        <v-card-title class="headline">
          {{ selectedPlatform ? formatPlatformName(selectedPlatform) : "" }}
          Locations - {{ formatStatusText(selectedStatus) }}
        </v-card-title>
        <v-card-text>
          <v-text-field
            v-model="locationSearchQuery"
            label="Search locations"
            prepend-inner-icon="mdi-magnify"
            clearable
            outlined
            dense
            class="mb-4"
          ></v-text-field>
          <v-list>
            <v-list-item
              v-for="location in filteredLocations"
              :key="location.location_id"
            >
              <v-list-item-content>
                <v-list-item-title>{{ location.name }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item v-if="filteredLocations.length === 0">
              <v-list-item-content>
                <v-list-item-title class="text-center text-grey">
                  {{
                    locationSearchQuery
                      ? "No matching locations found"
                      : "No locations available"
                  }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="showLocationDialog = false"
            >Close</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {
  auth,
  colUsers,
  colScheduledPostsDelete,
  colSocialPostsDelete,
} from "@/utils/firebase.utils";
import VideoAutoPlay from "../public/VideoAutoPlay.vue";
import PostOutput from "./PostOutput.vue";
import { deleteDoc, doc, getDoc, updateDoc, setDoc } from "firebase/firestore";
import { functions } from "@/utils/firebase.utils";
import { httpsCallable } from "firebase/functions";
import {
  collection,
  getDocs,
  query,
  where,
  onSnapshot,
} from "firebase/firestore";

export default {
  name: "PostCard",
  components: { VideoAutoPlay, PostOutput },
  props: {
    tab: {
      type: Number,
    },
    post: {
      type: Object,
      default: () => ({}),
    },
    openOutput: {
      type: Boolean,
      default: false,
    },
    is_cross_post: {
      type: Boolean,
      default: false,
    },
    showPreviewIcon: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      showFullText: false,
      carousel: 0,
      showOutput: false,
      draftLoading: false,
      checkUser: false,
      loading: false,
      approveButtonText: "",
      openRejectDialog: false,
      rejectComment: "",
      postId: this.$route.params.id,
      locationNames: [],
      platformStatusMap: {},
      unsubscribers: [],
      // Add showLocationDialog functionality
      showLocationDialog: false,
      selectedPlatform: null,
      selectedStatus: null,
      platformStatusPerPost: {},
      locationSearchQuery: "",
    };
  },
  created() {
    console.log("PostCard component created");
    console.log("Post data:", this.post);
    if (this.post && this.post.location_postIds) {
      console.log("Fetching location names...");
      this.fetchLocationNames();
      console.log("Fetching location post status...");
    } else {
      console.log("No location_postIds found in post data");
    }
    if (this.post?.location_postIds) {
      this.setupStatusListener();
    }
  },
  computed: {
    postedLocationCount() {
      return this.post.location_postIds ? this.post.location_postIds.length : 0;
    },
    checkUID() {
      if (
        auth.currentUser.uid == this.currentUID ||
        (this.post.post_status == "draft" &&
          this.post.postBy == auth.currentUser.uid)
      )
        return true;
      else return false;
    },
    shortText() {
      const maxLength = 100; // Max length of visible text when not expanded
      if (this.post.text && this.post.text.length > maxLength) {
        return this.post.text.substring(0, maxLength) + "...";
      }
      return this.post.text;
    },
    carousels() {
      let list = [];
      if (this.post.videos) {
        this.post.videos.forEach((item) => {
          list.push({
            src: item.url || item,
            type: "video",
          });
        });
      }
      if (this.post.images) {
        this.post.images.forEach((item) => {
          list.push({
            src: item.thumbnail || item.url || item,
            type: "image",
          });
        });
      }
      if (this.$route.params?.uid === auth.currentUser.uid) {
        this.checkUser = true;
        if (this.post.scheduled_at)
          this.approveButtonText = "Approve and Schedule";
        else this.approveButtonText = "Approve and Publish";
      }
      return list;
    },
    hasIg() {
      return (
        this.post.social_media?.includes("instagram") ||
        this.post.social_media?.some((social) => social?.name === "instagram")
      );
    },
    hasTh() {
      return (
        this.post.social_media?.includes("threads") ||
        this.post.social_media?.some((social) => social?.name === "threads")
      );
    },
    platformStats() {
      if (!this.post.location_postIds) return null;

      const stats = {};
      console.log("Fetching location stats..");
      this.post.location_postIds.forEach((locationPost) => {
        if (locationPost.platform) {
          if (!stats[locationPost.platform]) {
            stats[locationPost.platform] = {
              total: 0,
              published: 0,
              failed: 0,
              pending: 0,
            };
          }

          stats[locationPost.platform].total++;

          // Use the fetched status from platformStatusMap
          const status =
            this.platformStatusMap[
              `${locationPost.location_id}_${locationPost.platform}`
            ];
          // if (status) {
          //   switch (status.toLowerCase()) {
          //     case 'published':
          //       stats[locationPost.platform].published++;
          //       break;
          //     case 'posted':
          //       stats[locationPost.platform].published++;
          //       break;
          //     case 'failed':
          //       stats[locationPost.platform].failed++;
          //       break;
          //     default:
          //       //stats[locationPost.platform].pending++;
          //   }
          // } else {
          //   //stats[locationPost.platform].pending++;
          // }
        }
      });

      return Object.keys(stats).length ? stats : null;
    },
    filteredLocations() {
      if (
        !this.locationNames ||
        !this.selectedPlatform ||
        !this.selectedStatus
      ) {
        return [];
      }

      let filtered = this.locationNames
        .map((location) => {
          const postStatus =
            this.platformStatusPerPost[location.post_id]?.[
              this.selectedPlatform
            ];
          let status = "pending";

          if (postStatus) {
            if (postStatus.published > 0) status = "published";
            else if (postStatus.failed > 0) status = "failed";
          }

          return {
            ...location,
            status,
          };
        })
        .filter((location) => location.status === this.selectedStatus);

      // Add search filtering
      if (this.locationSearchQuery) {
        const searchLower = this.locationSearchQuery.toLowerCase();
        filtered = filtered.filter((location) =>
          location.name.toLowerCase().includes(searchLower)
        );
      }

      return filtered;
    },
  },
  methods: {
    async fetchLocationNames() {
      if (
        !this.post.location_postIds ||
        !Array.isArray(this.post.location_postIds)
      ) {
        console.warn("No valid location_postIds array found in post data.");
        return;
      }

      const fetchLocationPromises = this.post.location_postIds.map(
        async (locationPost) => {
          if (!locationPost.location_id) {
            console.warn("Missing location_id in locationPost:", locationPost);
            return { ...locationPost, name: "Unknown Location" };
          }

          try {
            const locationDocRef = doc(
              colUsers,
              this.post.uid,
              "fly_workspaces",
              locationPost.location_id // Directly use location_id here
            );
            const locationDoc = await getDoc(locationDocRef);
            if (locationDoc.exists()) {
              return {
                ...locationPost,
                name: `${locationDoc.data().name}, ${
                  locationDoc.data()?.sub_locality || locationDoc.data()?.city
                }`, // Assume location document has a 'name' field
                status: "pending",
              };
            } else {
              console.warn(
                "Location document not found for ID:",
                locationPost.location_id
              );
              return {
                ...locationPost,
                name: "Unknown Location",
                status: "pending",
              };
            }
          } catch (error) {
            console.error("Error fetching location document:", error);
            return {
              ...locationPost,
              name: "Error Loading Location",
              status: "pending",
            };
          }
        }
      );

      // Wait for all fetches to complete and store them
      this.locationNames = await Promise.all(fetchLocationPromises);
    },
    formatHashtags(tags) {
      if (tags) {
        return tags
          .map((i) => {
            if (i.toString().startsWith("#")) return i;
            return `#${i}`;
          })
          .map((i) => i.split(" ").join(""))
          .toString()
          .split(",")
          .join(" ");
      }
      return "";
    },
    copyLink() {
      let uid = this.post.uid || this.$route.params.uid;
      let wid = this.post.wid || this.$route.params.wid || this.WID;
      let id = this.post.id || this.$route.params.id;
      const route = this.$router.resolve({
        name: "post",
        params: {
          uid: uid,
          wid: wid,
          id: id,
        },
      });
      let url = `${location.origin}${route.href}`;
      navigator.clipboard.writeText(url);
      this.$snackbar.show("URL Copied.");
    },
    openPostDetail() {
      let uid = this.post.uid || this.$route.params.uid;
      let wid = this.post.wid || this.$route.params.wid;
      let id = this.post.id || this.$route.params.id;
      this.$router.push({
        name: "post",
        params: {
          uid: uid,
          wid: wid,
          id: id,
        },
      });
    },
    isPostLive(post) {
      const currentDate = new Date();
      const scheduledDate = new Date(post.scheduled_at);
      console.log(scheduledDate.getTime() < currentDate.getTime());
      return scheduledDate.getTime() < currentDate.getTime();
    },
    editPost(postId) {
      let id = postId ? postId : this.$route.params.id;
      this.$router.push({
        name: "edit-post",
        params: {
          postId: id,
        },
      });
    },
    getScheduledDate(scheduledDate) {
      if (typeof scheduledDate == "object" && scheduledDate.seconds) {
        return this.formatDate(scheduledDate.seconds * 1000);
      } else if (typeof scheduledDate == "string") {
        return this.formatDate(scheduledDate);
      } else {
        return scheduledDate;
      }
    },
    editDraft(postId) {
      let id = postId ? postId : this.$route.params.id;
      this.$router.push({
        name: "draft-post",
        params: {
          postId: id,
        },
      });
    },
    repost(postId) {
      let id = postId ? postId : this.$route.params.id;
      this.$router.push({
        name: "repost",
        params: {
          postId: id,
        },
      });
    },
    deletePost(post) {
      let postId = this.$route.params.id ? this.$route.params.id : post.id;
      let data = {
        uid: post.uid,
        wid: post.wid,
        postId: postId,
        deleted_at: new Date(),
      };
      let isLocalSeoPosts = false;
      if (post.location_postIds && post.location_postIds.length > 0) {
        isLocalSeoPosts = true;
      }
      let message = "";
      try {
        if (this.hasIg) {
          message = "Instagram: Post needs to be deleted manually";
        }
        if (this.hasTh) {
          message = "Threads: Post needs to be deleted manually";
        }
        this.$confirm.show({
          message: `Are you sure you want to delete this post?`,
          note: message,
          onConfirm: async () => {
            const postRef = doc(
              colUsers,
              post.uid,
              "fly_workspaces",
              post.wid,
              "social-posts",
              postId
            );
            let isPostPublished = this.isPostLive(post);
            try {
              if (post.post_status === "draft") {
                this.$loader.show(`Deletion in progress`);
                updateDoc(postRef, {
                  post_status: "deleted",
                });
                this.$snackbar.show("Post deleted successfully!");
                this.$emit("onPostDelete");
                this.$loader.hide();
                return;
              } else if (post.scheduled_at && !isPostPublished) {
                data.was_scheduled_for = new Date(post.scheduled_at);
                data.action = "delete";
                this.$snackbar.show(
                  "Your scheduled posts have been marked for deletion."
                );
                //mark this post for deletion in Firebase in fly_scheduled_posts_delete_requests collection
                await updateDoc(postRef, {
                  post_status: "deleted",
                });
                await setDoc(doc(colScheduledPostsDelete, postId), data);
                if (post.location_postIds && post.location_postIds.length > 0) {
                  const locationDeletePromises = post.location_postIds.map(
                    async (locationPost) => {
                      const locationData = {
                        uid: post.uid,
                        wid: locationPost.location_id,
                        postId: locationPost.post_id,
                        deleted_at: new Date(),
                        was_scheduled_for: new Date(post.scheduled_at),
                        action: "delete",
                      };
                      return setDoc(
                        doc(colScheduledPostsDelete, locationPost.post_id),
                        locationData
                      );
                    }
                  );
                  await Promise.all(locationDeletePromises);
                }
              } else {
                this.$snackbar.show(
                  "Your published posts have been marked for deletion."
                );
                //mark this post for deletion in Firebase in fly_published_posts_delete_requests collection
                await updateDoc(postRef, {
                  post_status: "deleted",
                });
                if (isLocalSeoPosts) {
                  post.location_postIds.forEach(async (locationPost) => {
                    data.wid = locationPost.location_id;
                    data.postId = locationPost.post_id;
                    await setDoc(
                      doc(colSocialPostsDelete, locationPost.post_id),
                      data
                    );
                  });
                } else {
                  await setDoc(doc(colSocialPostsDelete, postId), data);
                }
              }
            } catch (error) {
              this.$loader.hide();
              console.error("Error calling Cloud Function:", error);
              this.$alert.show("Something went wrong");
            }
          },
        });
      } catch (error) {
        console.error("Error deleting post:", error);
      }
    },
    getStatusCount(platform, status) {
      if (!this.platformStatusMap[platform]) return 0;
      return this.platformStatusMap[platform][status] || 0;
    },
    getTotalStatusCount(status) {
      return Object.values(this.platformStatusMap).reduce((sum, stats) => {
        return sum + (stats[status] || 0);
      }, 0);
    },
    async setupStatusListener() {
      // Clear any existing listeners
      this.cleanupListeners();

      if (!this.post?.location_postIds || !this.post?.uid) {
        console.log("No location posts or UID found");
        return;
      }

      console.log(
        "Setting up status listeners for locations:",
        this.post.location_postIds
      );

      for (const locationPost of this.post.location_postIds) {
        if (!locationPost.location_id || !locationPost.post_id) {
          console.warn("Invalid location post data:", locationPost);
          continue;
        }

        try {
          const socialPostRef = doc(
            colUsers,
            this.post.uid,
            "fly_workspaces",
            locationPost.location_id,
            "social-posts",
            locationPost.post_id
          );

          const unsubscribe = onSnapshot(
            socialPostRef,
            (docSnapshot) => {
              if (!docSnapshot.exists()) {
                console.warn(
                  `No document found for location ${locationPost.location_id}`
                );
                return;
              }

              const postData = docSnapshot.data();
              if (!postData?.output) return;

              // Initialize per-platform counts for this post
              const perPlatformCounts = {};

              Object.entries(postData.output).forEach(([platform, output]) => {
                if (!perPlatformCounts[platform]) {
                  perPlatformCounts[platform] = {
                    published: 0,
                    failed: 0,
                    pending: 0,
                  };
                }

                if (Array.isArray(output?.data)) {
                  // Handle array of statuses
                  output.data.forEach((item) => {
                    const status = item?.status?.toLowerCase();
                    this.updateStatusCountForPost(
                      perPlatformCounts,
                      platform,
                      status
                    );
                  });
                } else {
                  // Handle single status
                  const status = output?.status?.toLowerCase();
                  this.updateStatusCountForPost(
                    perPlatformCounts,
                    platform,
                    status
                  );
                }
              });

              // Update platformStatusPerPost with counts for this post
              this.$set(
                this.platformStatusPerPost,
                locationPost.post_id,
                perPlatformCounts
              );

              // Recompute the overall platformStatusMap
              this.recomputePlatformStatusMap();
            },
            (error) => {
              console.error(
                `Error listening to location ${locationPost.location_id}:`,
                error
              );
            }
          );

          this.unsubscribers.push(unsubscribe);
          console.log(
            `Listener set up for location ${locationPost.location_id}`
          );
        } catch (error) {
          console.error(
            `Error setting up listener for location ${locationPost.location_id}:`,
            error
          );
        }
      }
    },
    updateStatusCountForPost(perPlatformCounts, platform, status) {
      if (status === "published" || status === "posted") {
        perPlatformCounts[platform].published += 1;
      } else if (status === "error") {
        perPlatformCounts[platform].failed += 1;
      } else {
        perPlatformCounts[platform].pending += 1;
      }
    },
    recomputePlatformStatusMap() {
      const combinedPlatformCounts = {};

      // Loop over each post's platform counts
      Object.values(this.platformStatusPerPost).forEach((perPlatformCounts) => {
        // For each platform in this post
        Object.keys(perPlatformCounts).forEach((platform) => {
          if (!combinedPlatformCounts[platform]) {
            combinedPlatformCounts[platform] = {
              published: 0,
              failed: 0,
              pending: 0,
            };
          }

          combinedPlatformCounts[platform].published +=
            perPlatformCounts[platform].published;
          combinedPlatformCounts[platform].failed +=
            perPlatformCounts[platform].failed;
          combinedPlatformCounts[platform].pending +=
            perPlatformCounts[platform].pending;
        });
      });

      // Update the platformStatusMap with the combined counts
      this.platformStatusMap = combinedPlatformCounts;
    },
    cleanupListeners() {
      console.log(`Cleaning up ${this.unsubscribers?.length || 0} listeners`);
      if (this.unsubscribers) {
        this.unsubscribers.forEach((unsubscribe) => {
          try {
            unsubscribe();
          } catch (error) {
            console.error("Error during listener cleanup:", error);
          }
        });
      }
      this.unsubscribers = [];
      this.platformStatusPerPost = {}; // Reset per-post counts
      this.platformStatusMap = {}; // Reset overall counts
    },
    formatPlatformName(platform) {
      if (platform === "google_business_profile") {
        return "Google Business Profile";
      }
      return platform;
    },
    showLocationDetails(platform, status) {
      this.selectedPlatform = platform;
      this.selectedStatus = status;
      this.showLocationDialog = true;
    },
    formatStatusText(status) {
      const statusMap = {
        published: "Published",
        failed: "Failed",
        pending: "Pending",
      };
      return statusMap[status] || status;
    },
  },
  mounted() {
    console.log("PostCard component mounted");
  },
  watch: {
    post: {
      immediate: true,
      deep: true,
      handler(newPost) {
        console.log("Post data changed:", newPost);
        if (newPost && newPost.location_postIds) {
          this.setupStatusListener();
        }
      },
    },
  },
  beforeDestroy() {
    this.cleanupListeners();
  },
};
</script>

<style scoped>
.status-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 9999px;
  font-size: 0.7rem;
  font-weight: 500;
  line-height: 1.2;
}
.status-badge-published {
  background-color: #e0f2fe;
  color: #0284c7;
}
.status-badge-deleted {
  background-color: #fee2e2;
  color: #dc2626;
}
.status-badge-scheduled {
  background-color: #dcfce7;
  color: #16a34a;
}
.status-badge-draft {
  background-color: #ffedd5;
  color: #f97316;
}

@media (min-width: 1024px) {
  .images {
    height: 100%;
  }
}
@media (min-width: 768px) {
}
.media-carousel-side .v-window__prev,
.media-carousel-side .v-window__next {
  width: 24px;
  height: 24px;
  background-color: rgba(0, 0, 0, 0.4);
  border-radius: 50%;
  margin: 0 2px;
}

.media-carousel-side .v-window__prev .v-icon,
.media-carousel-side .v-window__next .v-icon {
  font-size: 16px !important;
  color: white;
}

.eye-icon {
  font-size: 1.1rem;
}

.platform-status-table {
  border-collapse: collapse;
  font-size: 0.7rem;
  background-color: #f8f9fa;
  border-radius: 0.375rem;
  border: 1px solid #e2e8f0;
}

.platform-status-table th {
  background-color: #edf2f7;
  font-weight: 600;
  padding: 4px 6px;
}

.platform-status-table td,
.platform-status-table th {
  border: 1px solid #e2e8f0;
  padding: 4px 6px;
}

.pending-loader-container {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.pending-loader {
  width: 20px;
  height: 2px;
  background-color: #8056de;
  position: relative;
  overflow: hidden;
  border-radius: 2px;
}

.pending-loader::after {
  content: "";
  position: absolute;
  width: 30%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.8);
  animation: pendingAnimation 1s infinite ease-in-out;
}

@keyframes pendingAnimation {
  0% {
    left: -30%;
  }
  100% {
    left: 100%;
  }
}

.timestamp-container {
  display: flex;
  gap: 8px;
  margin-bottom: 4px;
}

.timestamp-badge {
  display: flex;
  align-items: center;
  background-color: #f8fafc;
  border-radius: 4px;
  padding: 3px 6px;
  max-width: fit-content;
  border-left: 2px solid #818cf8;
}

.timestamp-badge.scheduled {
  border-left: 2px solid #10b981;
}

.timestamp-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 3px;
  color: #64748b;
}

.timestamp-badge.scheduled .timestamp-icon {
  color: #10b981;
}

.timestamp-info {
  display: flex;
  align-items: center;
  font-size: 0.65rem;
}

.timestamp-label {
  font-weight: 600;
  color: #64748b;
  margin-right: 2px;
}

.timestamp-badge.scheduled .timestamp-label {
  color: #047857;
}

.timestamp-value {
  font-weight: 500;
  color: #334155;
}

.timestamp-badge.scheduled .timestamp-value {
  color: #059669;
}
</style>
